import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbActiveModal, NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { differenceInDays } from 'date-fns';
import { AuthService, FilterService } from '../../../../../_shared/services';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { DashboardService } from '../../../services/dashboard.service';
import { DateInterval } from '../../../../../_shared/enums';
import { TimeSeriesGroupingHelper } from '../../../../../_shared/helpers/chart-grouping/time-series-grouping.helper';
import { calculateChartYTicks } from '../../../../../_shared/helpers/chart-y-ticks.helper';
import { AccountDateFilterRequest } from '../../../../../_shared/models/accountDateFilter.model';
import { SummaryRequest } from '../../../../../_shared/models';
import { MaintenanceInsightsResponse } from '../../../../maintenance/maintenance-dashboard/models/maintenance-dashboard-response.model';
import { EmptyStateComponent } from '../../../../../_shared/components/states/empty-state/empty-state.component';
import { GraphTrendIconComponent } from '../../../../../_shared/components/graphs/graph-trend-icon/graph-trend-icon.component';
import { AccountFilterComponent } from '../../../../../_shared/components/filters/account-filter/account-filter.component';
import { DateFilterComponent } from '../../../../../_shared/components/filters/date-filter/date-filter.component';
import { AccountFilterData, DateIntervalFilterData, WidgetFilterData } from '../../../../../_shared/models/filters/widget-filter-data.model';
import { isEmptyObject } from '../../../../../_shared/helpers/object.helper';
import {
  StackedBarChartComponent,
  StackedBarChartData,
  StackedBarSubgroup,
  LegendItem
} from '../../../../../_shared/components/graphs/stacked-bar-chart/stacked-bar-chart.component';
import { formatValue } from '../../../../../_shared/helpers/kpiWidget/kpi-formatters.helper';
import { CurrencyHelper } from '../../../../../_shared/helpers/currency/currency-map.helper';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-maintenance-cost-modal',
  standalone: true,
  imports: [
    TranslocoPipe,
    CommonModule,
    NgbDropdownModule,
    StackedBarChartComponent,
    EmptyStateComponent,
    GraphTrendIconComponent,
    AccountFilterComponent,
    DateFilterComponent
  ],
  templateUrl: './maintenance-cost-modal.component.html',
  styleUrl: './maintenance-cost-modal.component.scss'
})
export class MaintenanceCostModalComponent implements OnInit {
  private readonly transloco: TranslocoService = inject(TranslocoService);
  private activeModal = inject(NgbActiveModal);
  private filterService = inject(FilterService);
  private dashboardService = inject(DashboardService);
  private errorHandlerService = inject(ErrorHandlerService);
  private authService = inject(AuthService);

  chartData: StackedBarChartData[] = [];
  readonly chartHeight = 335;
  private readonly defaultYTicks = [1000000, 800000, 600000, 400000, 200000, 0];
  yTicks: number[] = this.defaultYTicks;

  // The chart component renders these strings itself, so they are rebuilt on every language change.
  subgroups: StackedBarSubgroup[] = [];
  legendItems: LegendItem[] = [];

  currencySymbol: string = '';
  currencyCode: string = '';

  isLoading: boolean = true;
  title: string = 'maintenanceDashboard.charts.maintenanceCost';
  accountFilter: AccountFilterData;
  dateFilter: DateIntervalFilterData;
  defaultInterval: DateInterval = DateInterval.Month;
  disabledIntervals: DateInterval[] = [DateInterval.Half];
  totalValue: number = 0;
  percentageChange: number = 0;
  private cancelSubject: Subject<void> = new Subject<void>();
  private modalFilter: WidgetFilterData = { ...this.filterService.filter };

  constructor() {
    this.accountFilter = {} as AccountFilterData;
    this.dateFilter = {} as DateIntervalFilterData;

    this.currencyCode = this.authService.getUserSettings()?.currencyCode ?? 'ZAR';
    this.currencySymbol = CurrencyHelper.getCurrencySymbol(this.currencyCode);

    // langChanges$ replays the active language, so this also does the initial build
    this.transloco.langChanges$.pipe(takeUntilDestroyed()).subscribe(() => this.buildChartLabels());
  }

  async ngOnInit(): Promise<void> {
    await this.loadMaintenanceCostData();
  }

  private buildChartLabels(): void {
    const preventative = this.transloco.translate('maintenanceDashboard.charts.preventative');
    const corrective = this.transloco.translate('maintenanceDashboard.charts.corrective');

    this.subgroups = [
      { name: 'preventative', legend: preventative, color: '#3B82F6' },
      { name: 'corrective', legend: corrective, color: '#F59E0B' }
    ];

    this.legendItems = [
      { legend: preventative, color: '#3B82F6' },
      { legend: corrective, color: '#F59E0B' }
    ];
  }

  async onAccountFilterChange(filter: AccountFilterData): Promise<void> {
    this.accountFilter = filter;
    this.updateFilter();
  }

  async onDateFilterChange(filter: DateIntervalFilterData): Promise<void> {
    this.dateFilter = filter;
    this.updateFilter();
  }

  async updateFilter(): Promise<void> {
    if (!isEmptyObject(this.dateFilter) && !isEmptyObject(this.accountFilter)) {
      this.modalFilter = {
        ...this.modalFilter,
        ...this.dateFilter,
        ...this.accountFilter
      };

      await this.loadMaintenanceCostData();
    }
  }

  async loadMaintenanceCostData(): Promise<void> {
    this.isLoading = true;

    try {
      const request: AccountDateFilterRequest = {
        accountIds: this.modalFilter.accountIds,
        timeFrom: this.modalFilter.startDate!,
        timeTo: this.modalFilter.endDate!,
        interval: this.modalFilter.interval,
      };

      const summaryRequest: SummaryRequest = {
        accountIds: this.modalFilter.accountIds,
        timeFrom: this.modalFilter.startDate!,
        timeTo: this.modalFilter.endDate!,
        previousTimeFrom: this.modalFilter.previousCycleStartDate!,
        previousTimeTo: this.modalFilter.previousCycleEndDate!
      };

      this.cancelSubject.next();

      const [preventativeResponse, correctiveResponse, summaryResponse] = await Promise.all([
        this.dashboardService.getPreventiveMaintenanceCostRatio(request, this.cancelSubject),
        this.dashboardService.getCorrectiveMaintenanceCostRatio(request, this.cancelSubject),
        this.dashboardService.getTotalMaintenanceCostStats(summaryRequest, this.cancelSubject)
      ]);

      this.chartData = this.processMaintenanceCostData(
        preventativeResponse.data ?? [],
        correctiveResponse.data ?? []
      );

      this.totalValue = summaryResponse?.data?.totalValue ?? 0;
      this.percentageChange = summaryResponse?.data?.percentageChange ?? 0;

      this.yTicks = this.calculateYTicks(this.chartData);
      this.isLoading = false;
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.isLoading = false;
          this.chartData = [];
          this.totalValue = 0;
          this.percentageChange = 0;
        }
      });
    }
  }

  private processMaintenanceCostData(
    preventativeData: MaintenanceInsightsResponse[],
    correctiveData: MaintenanceInsightsResponse[]
  ): StackedBarChartData[] {
    if (preventativeData.length === 0 && correctiveData.length === 0) return [];

    const daysDifference = differenceInDays(this.modalFilter.endDate!, this.modalFilter.startDate!);

    const datasets = [
      {
        label: 'preventative',
        data: preventativeData.map(item => ({
          totalValue: item.totalValue ?? 0,
          timeSeries: new Date(item.timeSeries)
        }))
      },
      {
        label: 'corrective',
        data: correctiveData.map(item => ({
          totalValue: item.totalValue ?? 0,
          timeSeries: new Date(item.timeSeries)
        }))
      }
    ];

    let groupedData: any[];

    // For 24 hours (1 day): show all 24 individual hours in modal
    if (daysDifference === 0) {
      groupedData = TimeSeriesGroupingHelper.groupByHours(datasets, true);
    }
    // For 1 month (up to 31 days): show every day (no limit for modal)
    else if (daysDifference <= 31) {
      groupedData = TimeSeriesGroupingHelper.groupByDays(datasets, this.modalFilter.startDate!, this.modalFilter.endDate!);
    }
    // For longer periods: group by weeks
    else {
      groupedData = TimeSeriesGroupingHelper.groupByWeeks(datasets, this.modalFilter.startDate!, this.modalFilter.endDate!);
    }

    return groupedData.map(item => ({
      label: item.label,
      dateRange: item.dateRange,
      preventative: Number(item['preventative']) || 0,
      corrective: Number(item['corrective']) || 0
    }));
  }

  private calculateYTicks(chartData: StackedBarChartData[]): number[] {
    if (!chartData?.length) return this.defaultYTicks;

    const stackedTotals = chartData.map(item => {
      return (Number(item['preventative']) || 0) + (Number(item['corrective']) || 0);
    });

    return calculateChartYTicks(stackedTotals, this.defaultYTicks);
  }

  formatPercentChange(): string {
    const sign = this.percentageChange > 0 ? '+' : '';
    return `${sign}${this.percentageChange.toLocaleString('default', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;
  }

  isUptrendPositive(): boolean {
    return false; // Cost increase is negative
  }

  formatValue(): string {
    return formatValue(this.totalValue, { currencySymbol: this.currencySymbol });
  }

  hasData(): boolean {
    if (!this.chartData || this.chartData.length === 0) return false;

    return this.chartData.some(item => {
      return this.subgroups.some(subgroup => {
        const value = Number(item[subgroup.name]) || 0;
        return value > 0;
      });
    });
  }

  onExport(): void {
    // TODO: Implement export functionality
    console.log('Export clicked');
  }

  close(): void {
    this.activeModal.close();
  }
}